
"use client";

import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import type { Client } from '@/lib/types';
import { getClient } from '@/components/clients/services/clients-api';
import { useAuth } from '@/contexts/AuthContext';
import logger from '@/lib/logging';

interface ClientContextType {
  client: Client | null;
  setClient: (client: Client | null) => void;
  isClientLoading: boolean;
}

/** Exported for optional-hooks.ts — prefer useClient() in normal usage. */
export const ClientContext = createContext<ClientContextType | undefined>(undefined);

/**
 * localStorage key. Only the client's *id* is persisted here — never the full
 * client document (name, contacts, medical, lifestyle are PHI and must not
 * sit at rest in localStorage, and a cached full object also goes stale the
 * moment the client is edited elsewhere). The full record is always re-fetched
 * from the server on mount.
 */
const SELECTED_CLIENT_ID_KEY = 'selectedClientId';
/** Legacy key from before this migration — held the full client JSON (PHI at rest). Purged on every mount and on every setClient call. */
const LEGACY_SELECTED_CLIENT_KEY = 'selectedClient';

export function ClientProvider({ children }: { children: ReactNode }) {
  const [client, setClientState] = useState<Client | null>(null);
  const [isClientLoading, setIsClientLoading] = useState(true);
  const { user, loading: authLoading } = useAuth();
  const clientRef = useRef<Client | null>(null);
  clientRef.current = client;

  useEffect(() => {
    // Wait for auth to settle before attempting an authenticated fetch —
    // the provider mounts inside AuthProvider, but the Firebase user may not
    // be ready yet on first render.
    if (authLoading) return;

    let cancelled = false;

    async function rehydrate() {
      let storedId: string | null = null;
      try {
        storedId = localStorage.getItem(SELECTED_CLIENT_ID_KEY);

        // Migrate the legacy full-JSON key: pull the id out of it (if present),
        // then always remove it so any previously-persisted PHI blob is purged
        // regardless of whether migration succeeds.
        const legacyRaw = localStorage.getItem(LEGACY_SELECTED_CLIENT_KEY);
        if (legacyRaw) {
          if (!storedId) {
            try {
              const legacyClient = JSON.parse(legacyRaw) as Partial<Client>;
              if (legacyClient?.id) {
                storedId = legacyClient.id;
                // Persist the migrated id under the new id-only key immediately —
                // otherwise, since the legacy key is removed below, the id would
                // only live in memory for this session and be lost on next reload.
                localStorage.setItem(SELECTED_CLIENT_ID_KEY, storedId);
              }
            } catch (parseError) {
              logger.warn('Could not parse legacy selectedClient value during migration:', parseError);
            }
          }
          localStorage.removeItem(LEGACY_SELECTED_CLIENT_KEY);
        }
      } catch (error) {
        logger.error('Could not access localStorage to load client:', error);
      }

      if (!storedId) {
        setIsClientLoading(false);
        return;
      }

      if (!user) {
        // No authenticated user (guest session that hasn't attached, or logged
        // out) — nothing we can fetch with; leave the stored id for a later
        // rehydration attempt once auth is available.
        setIsClientLoading(false);
        return;
      }

      try {
        const fetched = await getClient(storedId);
        if (cancelled) return;
        if (fetched) {
          setClientState(fetched);
        } else {
          // Not found / no access — clear the stale id so we don't keep retrying.
          logger.warn(`Stored selectedClientId "${storedId}" could not be fetched — clearing.`);
          localStorage.removeItem(SELECTED_CLIENT_ID_KEY);
        }
      } catch (error) {
        if (cancelled) return;
        logger.warn(`Failed to rehydrate selected client "${storedId}":`, error);
        try {
          localStorage.removeItem(SELECTED_CLIENT_ID_KEY);
        } catch {
          // localStorage inaccessible — nothing more we can do.
        }
      } finally {
        if (!cancelled) setIsClientLoading(false);
      }
    }

    void rehydrate();

    return () => {
      cancelled = true;
    };
  }, [authLoading, user]);

  const setClient = (newClient: Client | null) => {
    // Cheap no-op guard: skip the state update (and localStorage write) when
    // the new client is deep-equal to the current one. This is what makes it
    // safe for callers (e.g. ClientDetailPage's snapshot effect) to call
    // setClient on every Firestore snapshot without re-rendering every consumer.
    if (JSON.stringify(newClient) === JSON.stringify(clientRef.current)) {
      return;
    }

    setClientState(newClient);
    try {
      if (newClient) {
        localStorage.setItem(SELECTED_CLIENT_ID_KEY, newClient.id);
      } else {
        localStorage.removeItem(SELECTED_CLIENT_ID_KEY);
      }
      // Always purge the legacy PHI-bearing key, regardless of newClient.
      localStorage.removeItem(LEGACY_SELECTED_CLIENT_KEY);
    } catch (error) {
      logger.error('Could not access localStorage to save client:', error);
    }
  };

  return (
    <ClientContext.Provider value={{ client, setClient, isClientLoading }}>
      {children}
    </ClientContext.Provider>
  );
}

export function useClient() {
  const context = useContext(ClientContext);
  if (context === undefined) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
}
